import { useState, useEffect, useRef, useCallback } from 'react';
import { Color } from '@chess-platform/shared';

interface UseChessClockProps {
    timeWInitial: number | null;
    timeBInitial: number | null;
    turn: Color;
    isGameOver: boolean;
    isActive?: boolean;
    onTimeOut: (loserColor: Color) => void;
}

// Use requestAnimationFrame for a robust clock loop that doesn't pause
// when tabs lose focus as easily, and uses real elapsed time for accuracy.
export const useChessClock = ({
    timeWInitial,
    timeBInitial,
    turn,
    isGameOver,
    isActive = true,
    onTimeOut
}: UseChessClockProps) => {
    const [timeW, setTimeW] = useState<number | null>(timeWInitial);
    const [timeB, setTimeB] = useState<number | null>(timeBInitial);
    const rafRef = useRef<number | null>(null);
    const lastTsRef = useRef<number | null>(null);
    const accumulatedMsRef = useRef(0);
    const runningRef = useRef(false);
    const timeOutReportedRef = useRef(false);

    useEffect(() => {
        setTimeW(timeWInitial);
        setTimeB(timeBInitial);
        timeOutReportedRef.current = false;
    }, [timeWInitial, timeBInitial]);

    useEffect(() => {
        const tick = (ts: number) => {
            if (!lastTsRef.current) lastTsRef.current = ts;
            const deltaMs = ts - lastTsRef.current;
            lastTsRef.current = ts;

            const deltaSec = deltaMs / 1000;

            if (!runningRef.current || !isActive) return;

            // Use a threshold to update state only every ~100ms to save performance
            // while keeping the internal clock accurate.
            accumulatedMsRef.current += deltaMs;

            if (accumulatedMsRef.current >= 100) {
                const step = accumulatedMsRef.current / 1000;
                accumulatedMsRef.current = 0;

                if (turn === 'w') {
                    setTimeW(t => {
                        if (t === null) return null;
                        const next = +(t - step).toFixed(1);
                        if (next <= 0 && !timeOutReportedRef.current) {
                            timeOutReportedRef.current = true;
                            onTimeOut('w');
                            return 0;
                        }
                        return next > 0 ? next : 0;
                    });
                } else {
                    setTimeB(t => {
                        if (t === null) return null;
                        const next = +(t - step).toFixed(1);
                        if (next <= 0 && !timeOutReportedRef.current) {
                            timeOutReportedRef.current = true;
                            onTimeOut('b');
                            return 0;
                        }
                        return next > 0 ? next : 0;
                    });
                }
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        if (isGameOver) {
            runningRef.current = false;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            lastTsRef.current = null;
            return;
        }

        // start loop
        runningRef.current = true;
        lastTsRef.current = null;
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            runningRef.current = false;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            lastTsRef.current = null;
        };
        // Intentionally not listing timeW/timeB so the loop isn't restarted every tick
    }, [turn, isGameOver, onTimeOut, isActive]);

    const formatTime = useCallback((seconds: number | null) => {
        if (seconds === null) return '--:--';
        if (seconds >= 3600) {
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    }, []);

    return {
        timeW,
        timeB,
        setTimeW,
        setTimeB,
        formatTime
    };
};
